import { apiGet, cancelSelectEvent, FACTS_ORIGIN, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEventHandler, WidgetEventType, WidgetEvent } from './../../types';

interface SentimentTrendPoint {
    date: string;
    avg_sentiment: number;
}

interface MarketPulseSeries {
    [symbol: string]: SentimentTrendPoint[];
}

interface MarketPulseConfig extends WidgetConfig {
    index?: string[];
    interval?: string;
    top_n?: number;
    min_articles?: number;
    onIndexChange?: (index: string[]) => void;
    onIntervalChange?: (interval: string) => void;
}

export class TraderverseSentimentTrendOverTime {
    private container: HTMLElement;
    private config: MarketPulseConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private chartInstance: any = null;
    private marketPulseData: MarketPulseSeries = {};
    private trendingIndexes: Array<{ value: string; label: string }> = [];
    private hiddenSeries: Set<string> = new Set();
    private colorSeries: string[] = [
        '#5D17B5',
        '#FF3B3B',
        '#4285F4',
        '#FFC300',
        '#34E36F'
    ];
    private widgetIndex: number = 0;

    constructor(config: MarketPulseConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }

        this.init();
    }

    private getDefaultConfig(): Partial<MarketPulseConfig> {
        return {
      headerPinned: true,
            theme: 'white',
            width: '100%',
            height: '400px',
            autosize: true,
            interval: 'thismonth',
            top_n: 5,
            min_articles: 10
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const element = document.getElementById(container) || document.querySelector(container);
            if (!element) {
                throw new Error(`Container not found: ${container}`);
            }
            return element as HTMLElement;
        }
        return container;
    }

    private async loadECharts(): Promise<void> {
        if (!(window as any).echarts) {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
            document.head.appendChild(script);

            return new Promise((resolve) => {
                script.onload = () => resolve();
            });
        }
    }

    private async init(): Promise<void> {
        await this.loadECharts();
        if (!this.config.index && this.trendingIndexes.length > 0) {
            this.config.index = [this.trendingIndexes[0].value];
        }
        if (!this.config.interval) {
            this.config.interval = 'thismonth';
        }
        if (this.config.top_n == null) {
            this.config.top_n = 5;
        }
        if (this.config.min_articles == null) {
            this.config.min_articles = 10;
        }

        this.attachStyles();
        await this.createWidget();
    setupHeaderPin(this.shadowRoot, this.config);
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.loadMarketPulseData();
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';
        
        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
   <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-testing-id="sentiment-trend-over-time-widget-${this.widgetIndex}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title news">${this.getTitleIconSvg()} Sentiment Trend Over Time for Top 5 Symbols</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
                <div class="optionsRow" data-testing-id="options-row">
                    <div class="item" data-testing-id="interval-selector">
                        <select id="intervalSelect" aria-label="Interval select" data-testing-id="interval-select">
                            <option value="today">Today</option>
                            <option value="thisweek">This Week</option>
                            <option value="thismonth">This Month</option>
                        </select>
                    </div>
                    <span></span>
                    <div class="item" data-testing-id="legends-container">
                        <div class="legendsCollection" data-testing-id="legends-collection">
                            <span class="toggle-series active" data-series-index="0" style="background-color:#5D17B5;" data-testing-id="legend-series-0" data-series-name="Series 0"></span>
                            <span class="toggle-series active" data-series-index="1" style="background-color:#FF3B3B;" data-testing-id="legend-series-1" data-series-name="Series 1"></span>
                            <span class="toggle-series active" data-series-index="2" style="background-color:#4285F4;" data-testing-id="legend-series-2" data-series-name="Series 2"></span>
                            <span class="toggle-series active" data-series-index="3" style="background-color:#FFC300;" data-testing-id="legend-series-3" data-series-name="Series 3"></span>
                            <span class="toggle-series active" data-series-index="4" style="background-color:#34E36F;" data-testing-id="legend-series-4" data-series-name="Series 4"></span>
                        </div>
                    </div>
                </div>
                <div class="chartDv" data-testing-id="chart-container">
                    <div id="chart" class="coverDv" data-testing-id="sentiment-trend-chart"></div>
                </div>

        </div>
    </div>
    `;

        this.shadowRoot.innerHTML = widgetHtml;

        const intervalSelect = this.shadowRoot.getElementById('intervalSelect') as HTMLSelectElement | null;
        if (intervalSelect) {
            intervalSelect.value = this.config.interval || 'thismonth';
            intervalSelect.addEventListener('change', async (e: Event) => {
                const val = (e.target as HTMLSelectElement).value;
                this.config.interval = val;
                const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
                if (widget) widget.classList.add('loading');
                await this.loadMarketPulseData();
                if (widget) widget.classList.remove('loading');
            });
        }

        this.setupLegendToggles();
        this.initializeChart();
    }

    private setupLegendToggles(): void {
        const legendSpans = this.shadowRoot.querySelectorAll('.toggle-series');
        
        legendSpans.forEach((span) => {
            span.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                const seriesIndex = parseInt(target.getAttribute('data-series-index') || '0');
                
                target.classList.toggle('active');
                this.toggleSeries(seriesIndex, target.classList.contains('active'));
            });
        });
    }

    private toggleSeries(seriesIndex: number, isVisible: boolean): void {
        if (!this.chartInstance) return;
    
        const option = this.chartInstance.getOption();
        
        if (option && option.series && option.series[seriesIndex]) {
            const newOption = {
                ...option,
                series: option.series.map((series: any, index: number) => {
                    if (index === seriesIndex) {
                        return {
                            ...series,
                            lineStyle: {
                                ...series.lineStyle,
                                opacity: isVisible ? 1 : 0
                            },
                            areaStyle: {
                                ...series.areaStyle,
                                opacity: isVisible ? 0.6 : 0
                            }
                        };
                    }
                    return series;
                })
            };
    
            this.chartInstance.setOption(newOption);
    
            const symbol = option.series[seriesIndex]?.name;
            if (symbol) {
                if (isVisible) {
                    this.hiddenSeries.delete(symbol);
                } else {
                    this.hiddenSeries.add(symbol);
                }
            }
        }
    }

    private async initializeChart(): Promise<void> {
        console.log('Initializing chart...');
        await this.loadECharts();

        setTimeout(() => {
            this.renderChart();
            this.setupResizeObserver();
        }, 300);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private renderChart(): void {
        const chartElement = this.shadowRoot.getElementById('chart');
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        
        if (!chartElement) {
            console.error('Chart element not found');
            return;
        }

        const rect = chartElement.getBoundingClientRect();
        if (rect.height === 0) {
            chartElement.style.height = '350px';
            chartElement.style.width = '100%';
        }

        const echarts = (window as any).echarts;
        if (!echarts) {
            console.error('ECharts library not loaded');
            return;
        }

        if (this.chartInstance) {
            this.chartInstance.dispose();
        }
        
        if (!this.marketPulseData || Object.keys(this.marketPulseData).length === 0) {
            chartElement.innerHTML = getNoDataHTML();
            return;
        }

        this.chartInstance = echarts.init(chartElement);

        const widgetBoxComputed = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBoxComputed).getPropertyValue('--charts-text-color');
        const axisLineColor = getComputedStyle(widgetBoxComputed).getPropertyValue('--charts-axis-color');
        const splitLineColor = getComputedStyle(widgetBoxComputed).getPropertyValue('--charts-split-line-color');

        const series: any[] = [];
        const allDates: Set<string> = new Set();
        const colorSeries = this.colorSeries;

        Object.entries(this.marketPulseData).forEach(([symbol, data], index) => {
            data.forEach((item: any) => allDates.add(item.date));

            const sortedData = data
                .map((item: any) => [new Date(item.date).getTime(), item.avg_sentiment])
                .sort((a, b) => a[0] - b[0]);

            const isHidden = this.hiddenSeries.has(symbol);

            series.push({
                name: symbol,
                type: 'line',
                smooth: true,
                showSymbol: false,
                symbolSize: 6,
                lineStyle: { 
                    width: 0,
                    opacity: isHidden ? 0 : 1
                },
                itemStyle: { opacity: 0.9 },
                areaStyle: {
                    opacity: isHidden ? 0 : 0.6,
                    color: colorSeries[index],
                },
                data: sortedData,
            });
        });

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                ...getEChartsTooltipTheme(widgetBoxComputed),
                formatter: (params: any) => {
                    const date = new Date(params[0].value[0]);
                    let tooltip = `<b>${date.toLocaleDateString()}</b><br/>`;
                    params.forEach((p: any) => {
                        const seriesOption = this.chartInstance?.getOption()?.series?.[p.seriesIndex];
                        if (seriesOption && seriesOption.lineStyle?.opacity !== 0) {
                            tooltip += `${p.marker} <b>${p.seriesName}</b>: ${p.value[1].toFixed(3)}<br/>`;
                        }
                    });
                    return tooltip;
                },
            },
            grid: {
                left: 10,
                right: 10,
                top: 10,
                bottom: 10,
                containLabel: true,
            },
            xAxis: {
                type: 'time',
                axisLine: { lineStyle: { color: axisLineColor } },
                axisLabel: { color: textColor, fontSize: 12 },
                axisTick: { show: false },
                splitLine: { show: false },
            },
            yAxis: {
                type: 'value',
                axisLine: { lineStyle: { color: axisLineColor } },
                axisLabel: {
                    color: textColor,
                    formatter: (val: number) => val.toFixed(2),
                },
                splitLine: {
                    lineStyle: {
                        type: 'dashed',
                        color: splitLineColor,
                    },
                },
            },
            series,
        };

        this.chartInstance.setOption(option);
        
        // Add data-testing-id to chart after render
        setTimeout(() => {
            const chartSvg = this.shadowRoot.querySelector('#chart svg');
            if (chartSvg) {
                chartSvg.setAttribute('data-testing-id', 'sentiment-trend-chart-svg');
            }
        }, 500);
        
        try {
            this.updateLegendTooltips();
        } catch (err) {
            console.warn('Failed to update legend tooltips:', err);
        }

        setTimeout(() => {
            if (this.chartInstance) this.chartInstance.resize();
        }, 100);
    }

    private updateLegendTooltips(): void {
        const legendSpans = this.shadowRoot.querySelectorAll('.toggle-series');
        const option = this.chartInstance?.getOption ? this.chartInstance.getOption() : null;
        const seriesNames: string[] = [];

        if (option && option.series) {
            for (let i = 0; i < option.series.length; i++) {
                const s = option.series[i];
                seriesNames.push(s && s.name ? String(s.name) : `Series ${i + 1}`);
            }
        } else {
            const keys = Object.keys(this.marketPulseData || {});
            for (let i = 0; i < keys.length; i++) {
                seriesNames.push(keys[i] || `Series ${i + 1}`);
            }
        }

        legendSpans.forEach((span, idx) => {
            const name = seriesNames[idx] || `Series ${idx + 1}`;
            span.setAttribute('data-series-name', name);
            span.setAttribute('data-tooltip', name);
            span.setAttribute('aria-label', name);
        });
    }

    private async loadMarketPulseData(): Promise<void> {
        try {
            const data = await this.setupChart();
            this.marketPulseData = data && Object.keys(data).length > 0 ? data : {};
            this.renderChart();
        } catch (error) {
            console.error('Error loading sentiment trend data:', error);
            this.marketPulseData = {};
            this.renderChart();
        }
    }

    /** Maps widget interval values to `sentiment_trend_over_time` `time` query (e.g. 1month, 1week, today). */
    private mapIntervalToApiTime(interval?: string): string {
        const raw = (interval || this.config.interval || 'thismonth').toString().replace(/\s+/g, '_').toLowerCase();
        if (raw === 'today') return 'today';
        if (raw === 'thisweek' || raw === '1week') return '1week';
        if (raw === 'thismonth' || raw === '1month') return '1month';
        return raw;
    }

    private extractSentimentTrendOutput(responseData: any): any[] {
        if (!responseData) return [];
        if (Array.isArray(responseData)) return responseData;

        let dataBlock = responseData.data ?? responseData.Data;
        if (typeof dataBlock === 'string') {
            try {
                dataBlock = JSON.parse(dataBlock);
            } catch {
                return [];
            }
        }
        if (Array.isArray(dataBlock)) return dataBlock;
        if (dataBlock && typeof dataBlock === 'object') {
            const output = (dataBlock as any).output ?? (dataBlock as any).Output;
            if (Array.isArray(output)) return output;
        }
        if (Array.isArray(responseData.output)) return responseData.output;
        if (Array.isArray(responseData.result)) return responseData.result;
        return [];
    }

    private formatSentimentTrendSeries(output: any[]): MarketPulseSeries {
        const formatted: MarketPulseSeries = {};

        output.forEach((item: any) => {
            const symbol = item?.symbol;
            const rawDate = item?.date;
            if (!symbol || !rawDate) return;

            const avgRaw = item.avg_sentiment;
            const avg_sentiment =
                typeof avgRaw === 'number'
                    ? parseFloat(avgRaw.toFixed(4))
                    : parseFloat(parseFloat(String(avgRaw)).toFixed(4));
            if (!Number.isFinite(avg_sentiment)) return;

            if (!formatted[symbol]) formatted[symbol] = [];
            formatted[symbol].push({
                date: rawDate,
                avg_sentiment,
            });
        });

        Object.keys(formatted).forEach((symbol) => {
            formatted[symbol].sort(
                (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
            );
        });

        return formatted;
    }

    private async setupChart(): Promise<MarketPulseSeries> {
        const timeApi = this.mapIntervalToApiTime(this.config.interval);
        const topN = this.config.top_n ?? 5;
        const minArticles = this.config.min_articles ?? 10;

        try {
            const response = await apiGet({
                method: 'GET',
                url: `${FACTS_ORIGIN}/news/sentiment_trend_over_time`,
                query: {
                    time: timeApi,
                    top_n: topN,
                    min_articles: minArticles,
                },
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                console.error(`sentiment_trend_over_time HTTP ${response.status}`);
                return {};
            }

            const responseData = await response.json();
            if (responseData && typeof responseData.isSuccess === 'boolean' && !responseData.isSuccess) {
                console.error('sentiment_trend_over_time API returned isSuccess: false');
                return {};
            }

            const output = this.extractSentimentTrendOutput(responseData);
            return this.formatSentimentTrendSeries(output);
        } catch (error) {
            console.error('Error fetching sentiment trend data:', error);
            return {};
        }
    }

    public on(eventType: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(eventType)) {
            this.eventHandlers.set(eventType, []);
        }
        this.eventHandlers.get(eventType)!.push(handler);
    }

    public off(eventType: WidgetEventType, handler: WidgetEventHandler): void {
        const handlers = this.eventHandlers.get(eventType);
        if (handlers) {
            const index = handlers.indexOf(handler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;
      
        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    }

    public refresh(): void {
        this.loadMarketPulseData();
    }

    public resize(): void {
        if (this.chartInstance) {
            const chartElement = this.shadowRoot.getElementById('chart');
            if (chartElement) {
                const rect = chartElement.getBoundingClientRect();
                console.log('Resizing chart with current dimensions:', { width: rect.width, height: rect.height });

                if (rect.height === 0) {
                    chartElement.style.height = '350px';
                }
            }
            this.chartInstance.resize();
        }
    }

    private setupResizeObserver(): void {
        const chartElement = this.shadowRoot.getElementById('chart');
        if (chartElement && 'ResizeObserver' in window) {
            const resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    console.log('Chart element resized:', entry.contentRect);
                    if (this.chartInstance) {
                        setTimeout(() => this.chartInstance.resize(), 10);
                    }
                }
            });
            resizeObserver.observe(chartElement);
        }
    }

    public destroy(): void {
        if (this.chartInstance) {
            this.chartInstance.dispose();
        }
        this.eventHandlers.clear();
        this.hiddenSeries.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const event: WidgetEvent = { type, data };
            handlers.forEach(handler => handler(event));
        }
    }
    private getTitleIconSvg(): string {
      return `<svg width="27" height="22" viewBox="0 0 27 22" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M22.6125 22H4.3875C3.26681 22.0001 2.18858 21.5634 1.37399 20.7794C0.559395 19.9955 0.0701982 18.9239 0.00675018 17.7843L2.29482e-08 17.5312V3.09375C-9.45295e-05 2.30983 0.291999 1.5551 0.817249 0.982089C1.3425 0.409082 2.06173 0.0605304 2.8296 0.00687518L3.0375 2.33732e-08H19.9125C20.6822 -9.62801e-05 21.4232 0.297406 21.9858 0.832383C22.5484 1.36736 22.8906 2.09991 22.9433 2.882L22.95 3.09375V4.125H23.9625C24.7322 4.1249 25.4732 4.42241 26.0358 4.95738C26.5984 5.49236 26.9406 6.22491 26.9933 7.007L27 7.21875V17.5312C27.0001 18.6727 26.5713 19.7709 25.8016 20.6006C25.032 21.4302 23.9798 21.9285 22.8609 21.9931L22.6125 22ZM4.3875 19.9375H22.6125C23.2056 19.9374 23.777 19.7102 24.2132 19.3008C24.6494 18.8914 24.9184 18.33 24.9669 17.7279L24.975 17.5312V7.21875C24.975 6.96955 24.8864 6.72878 24.7256 6.54097C24.5647 6.35316 24.3426 6.23102 24.1002 6.19712L23.9625 6.1875H22.95V16.8438C22.95 17.093 22.8614 17.3337 22.7006 17.5215C22.5397 17.7093 22.3176 17.8315 22.0752 17.8654L21.9375 17.875C21.6928 17.875 21.4564 17.7847 21.272 17.6209C21.0876 17.4571 20.9677 17.2309 20.9345 16.984L20.925 16.8438V3.09375C20.925 2.84455 20.8364 2.60378 20.6756 2.41597C20.5147 2.22816 20.2926 2.10602 20.0502 2.07212L19.9125 2.0625H3.0375C2.79283 2.06251 2.55644 2.15276 2.37204 2.31656C2.18765 2.48035 2.06773 2.70661 2.03445 2.9535L2.025 3.09375V17.5312C2.02502 18.1351 2.24798 18.7169 2.64962 19.1612C3.05125 19.6054 3.6022 19.8796 4.1931 19.9292L4.3875 19.9375ZM13.8321 14.4375H17.8902C18.1467 14.4376 18.3937 14.5368 18.5811 14.7152C18.7686 14.8936 18.8826 15.1378 18.9 15.3985C18.9175 15.6591 18.8372 15.9169 18.6753 16.1195C18.5134 16.3222 18.282 16.4548 18.0279 16.4904L17.8902 16.5H13.8321C13.5756 16.4999 13.3286 16.4007 13.1412 16.2223C12.9537 16.0439 12.8398 15.7997 12.8223 15.539C12.8048 15.2784 12.8851 15.0206 13.047 14.818C13.2089 14.6153 13.4403 14.4827 13.6944 14.4471L13.8321 14.4375ZM9.77805 9.63188C10.0466 9.63188 10.3041 9.74052 10.494 9.93392C10.6839 10.1273 10.7906 10.3896 10.7906 10.6631V15.4688C10.7906 15.7423 10.6839 16.0046 10.494 16.198C10.3041 16.3914 10.0466 16.5 9.77805 16.5H5.0598C4.79127 16.5 4.53373 16.3914 4.34385 16.198C4.15397 16.0046 4.0473 15.7423 4.0473 15.4688V10.6631C4.0473 10.3896 4.15397 10.1273 4.34385 9.93392C4.53373 9.74052 4.79127 9.63188 5.0598 9.63188H9.77805ZM8.76555 11.6944H6.0723V14.4375H8.76555V11.6944ZM13.8321 9.63188H17.8902C18.1467 9.63196 18.3937 9.73121 18.5811 9.90959C18.7686 10.088 18.8826 10.3322 18.9 10.5928C18.9175 10.8535 18.8372 11.1112 18.6753 11.3139C18.5134 11.5166 18.282 11.6491 18.0279 11.6848L17.8902 11.6944H13.8321C13.5737 11.6971 13.3242 11.599 13.1344 11.4204C12.9447 11.2417 12.8293 10.996 12.8117 10.7334C12.7941 10.4709 12.8757 10.2114 13.0398 10.0082C13.2039 9.8049 13.4381 9.67324 13.6944 9.64012L13.8321 9.63188ZM5.0598 4.81525H17.8902C18.1486 4.81257 18.3981 4.9106 18.5879 5.08925C18.7776 5.2679 18.893 5.51366 18.9106 5.7762C18.9282 6.03875 18.8466 6.29821 18.6825 6.50147C18.5184 6.70472 18.2842 6.83639 18.0279 6.8695L17.8902 6.87775H5.0598C4.80327 6.87767 4.55633 6.77841 4.36888 6.60004C4.18143 6.42166 4.06745 6.17746 4.04997 5.91679C4.03248 5.65612 4.1128 5.3984 4.27468 5.19571C4.43657 4.99303 4.66796 4.86049 4.9221 4.82487L5.0598 4.81525Z" fill="currentColor"/> </svg>`;
    }

}

declare global {
    interface Window {
        TraderverseSentimentTrendOverTime: typeof TraderverseSentimentTrendOverTime;
    }
}

if (typeof window !== 'undefined') {
    window.TraderverseSentimentTrendOverTime = TraderverseSentimentTrendOverTime;
}